import React, { useEffect, useRef, useState } from "react";
import { Chip, ChipOption, ChipSizeEnum } from "../Chip/Chip";
import { Avatar, AvatarDimensionsEnum } from "../Avatar/Avatar";
import {
  CollaboratorInfo,
  FontWeightEnum,
  IconSizeEnum,
} from "../../../types/AppTypes";
import { getInitials } from "../../../utils/collaborators.utils";
import {
  StyledContentCollaboratorChip,
  StyledNameCollaboratorChip,
} from "./CollaboratorChip.styled";
import { IconManager } from "../IconManager/IconManager";
import { ReactComponent as FileCheckIcon } from "../../../icons/fileCheck.svg";
import { BodyTyp } from "../../Typography/Typography";
import { capitalize } from "../../../utils/string.utils";
import { theme } from "../../../constants/StyledComponents";
import { Tooltip } from "../Tooltip/Tooltip";

type CollaboratorChipCalibrationProps = {
  percentage: number;
  onClickPercentage?: (percentage: number) => void;
};

type CollaboratorChipProps = {
  size?: ChipSizeEnum;
  collaborator: CollaboratorInfo;
  borderColor?: string;
  backgroundColor?: string;
  onClick?: (x: CollaboratorInfo) => void;
  isDraggable?: boolean;
  calibration?: CollaboratorChipCalibrationProps;
};

export const CollaboratorChip = ({
  collaborator,
  borderColor,
  backgroundColor,
  onClick,
  size,
  isDraggable = false,
  calibration = undefined,
}: CollaboratorChipProps) => {
  const isSmall = size === ChipSizeEnum.SMALL;
  const fullName = capitalize(`${collaborator.surname} ${collaborator.name}`);
  const GAP = 6;

  const leftContentRef = useRef<HTMLDivElement>(null);
  const avatarRef = useRef<HTMLDivElement>(null);
  const [textMaxWidth, setTextMaxWidth] = useState<number>();

  useEffect(() => {
    const container = leftContentRef.current;
    const avatar = avatarRef.current;
    if (!container || !avatar) return;

    const observer = new ResizeObserver(() => {
      const available = container.clientWidth - avatar.clientWidth - GAP;
      setTextMaxWidth(Math.max(available, 30));
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const content: ChipOption<number> = {
    value: collaborator.id,
    label: (
      <StyledContentCollaboratorChip
        $isDraggable={isDraggable}
        $isCalibration={!!calibration}
      >
        <div className="left-content" ref={leftContentRef}>
          <div ref={avatarRef}>
            <Avatar
              variant={
                isSmall
                  ? AvatarDimensionsEnum.X_SMALL
                  : AvatarDimensionsEnum.SMALL
              }
              collaboratorId={collaborator.id}
              initials={getInitials(collaborator.name, collaborator.surname)}
            />
          </div>
          <Tooltip message={fullName} autoWidth>
            <StyledNameCollaboratorChip>
              <BodyTyp
                fontWeight={FontWeightEnum.MEDIUM}
                variant={isSmall ? "2" : "3"}
                noLineHeight
                maxWidth={textMaxWidth}
              >
                {fullName}
              </BodyTyp>
            </StyledNameCollaboratorChip>
          </Tooltip>
        </div>
        {calibration ? (
          <div
            className="percentage-container"
            onClick={() =>
              calibration.onClickPercentage?.(calibration.percentage)
            }
          >
            <BodyTyp
              fontWeight={FontWeightEnum.MEDIUM}
              variant={isSmall ? "2" : "3"}
              noLineHeight
            >
              {`${calibration.percentage}`}
            </BodyTyp>
          </div>
        ) : (
          <div className="icon-slot">
            <IconManager
              size={IconSizeEnum.XSMALL}
              {...(onClick && {
                onClick: () => onClick(collaborator),
              })}
            >
              <FileCheckIcon />
            </IconManager>
          </div>
        )}
      </StyledContentCollaboratorChip>
    ),
  };

  return (
    <Chip
      option={content}
      borderColor={calibration ? theme.colors.black : borderColor}
      backgroundColor={backgroundColor}
      size={size}
      fullWidth
      readonly
      borderWidth={2}
    />
  );
};
